import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LoopIcon from '@mui/icons-material/Loop';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/landing/Navigation';
import { HeroSection } from '../components/landing/HeroSection';
import { HowItWorksSection } from '../components/landing/HowItWorksSection';
import { FeaturesSection } from '../components/landing/FeaturesSection';
import { TestimonialsSection } from '../components/landing/TestimonialsSection';
import { CTASection } from '../components/landing/CTASection';
import { Footer } from '../components/landing/Footer';
import { WaitlistSection } from '../components/landing/WaitlistSection';
import { Feature } from '../components/landing/types';
import { useAuth } from '../contexts/AuthContext';

export function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, user, navigate]);

  const features: Feature[] = [
    {
      icon: <LoopIcon sx={{ fontSize: 40 }} />,
      title: 'Study Loops',
      description: 'Create custom learning cycles that adapt to your needs. Transform new information into long-term knowledge through continuous, focused repetition.'
    },
    {
      icon: <RefreshIcon sx={{ fontSize: 40 }} />,
      title: 'Review Loops',
      description: 'Automatically focus on challenging concepts with specialized Review Loops. Never waste time on what you already know—target what needs improvement.'
    },
    {
      icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
      title: 'AI Study Loop Generator',
      description: 'Generate custom study loops for any topic instantly. Our AI creates comprehensive learning cycles with carefully crafted questions to help you master any subject.'
    }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
      }}
    >
      <Helmet>
        <title>BrainLoop - AI Flashcard & Study Loop App</title>
        <meta name="description" content="BrainLoop uses AI to generate custom flashcard study loops for any subject. Master anything faster with spaced repetition, smart review loops, and AI-powered card creation." />
        <meta name="keywords" content="AI flashcard generator, study loop app, spaced repetition, AI study cards, flashcard app, exam prep, Anki alternative" />
        <link rel="canonical" href="https://brainloop.app/" />
        <meta property="og:title" content="BrainLoop - AI Flashcard & Study Loop App" />
        <meta property="og:description" content="BrainLoop uses AI to generate custom flashcard study loops for any subject. Master anything faster with spaced repetition and smart review loops." />
        <meta property="og:url" content="https://brainloop.app/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "BrainLoop",
          "url": "https://brainloop.app",
          "description": "AI-powered flashcard study loop app. Generate custom study loops for any subject, master anything faster with spaced repetition and smart review loops.",
          "applicationCategory": "EducationApplication",
          "operatingSystem": "Web",
          "offers": [
            {
              "@type": "Offer",
              "name": "Free",
              "price": "0",
              "priceCurrency": "USD"
            },
            {
              "@type": "Offer",
              "name": "Pro",
              "price": "9.99",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "billingDuration": "P1M"
              }
            }
          ],
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "1000"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "BrainLoop",
          "url": "https://brainloop.app",
          "logo": "https://brainloop.app/android-chrome-512x512.png",
          "description": "BrainLoop is an AI-powered flashcard and study loop platform that helps students master any subject faster through spaced repetition.",
          "sameAs": []
        })}</script>
      </Helmet>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: (theme) => `radial-gradient(ellipse at 30% 20%, ${alpha(theme.palette.common.white, 0.16)}, transparent 45%)`,
          opacity: 0.8,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: (theme) => `radial-gradient(ellipse at 75% 10%, ${alpha(theme.palette.common.white, 0.12)}, transparent 35%)`,
          opacity: 0.6,
          pointerEvents: 'none',
        }}
      />

      <Navigation />
      <Box component="main">
        <HeroSection />
        <HowItWorksSection />
        <WaitlistSection />
        <FeaturesSection features={features} />
        <TestimonialsSection />
        <CTASection />
      </Box>
      <Footer />
    </Box>
  );
} 